import { motion } from "framer-motion";
import { MovingParticles } from "@/components/MovingParticles";
import { useTheme } from "@/contexts/ThemeContext";

export const Hero = () => {
  const { colors } = useTheme();
  
  // Optimized animation variants for mobile
  const isMobile = window.innerWidth < 768;
  const animationDuration = isMobile ? 0.4 : 0.6;
  
  return (
    <section 
      id="home" 
      className="min-h-screen flex items-center justify-center px-4 py-0 relative overflow-hidden transition-colors duration-150"
      style={{ backgroundColor: colors.background, color: colors.foreground }}
    >
      <div className="max-w-6xl mx-auto text-center relative z-10 w-full">
        {/* Moving Particles */}
        <MovingParticles />
        
        {/* Simplified background - removed most background animations */}
        <div className="absolute inset-0 -z-10">
          {/* Keep only 2 static background elements for visual appeal */}
          <div 
            className="absolute top-20 left-10 w-32 h-32 md:w-64 md:h-64 lg:w-80 lg:h-80 rounded-full blur-3xl opacity-30"
            style={{ backgroundColor: `${colors.primary}03` }}
          />
          
          <div 
            className="absolute bottom-20 right-10 w-36 h-36 md:w-72 md:h-72 lg:w-96 lg:h-96 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: `${colors.primary}02` }}
          />
        </div>

        {/* Main content */}
        <div className="pt-24 sm:pt-16 md:pt-20 lg:pt-24 min-h-screen flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: animationDuration }}
            className="mb-12 sm:mb-8 md:mb-10 lg:mb-14 xl:mb-18"
          >
            {/* TITLE ANIMATION PRESERVED - This is the main visual element */}
            <motion.h1
              className="text-6xl sm:text-7xl xl:text-8xl font-bold text-center"
              style={{
                background: colors.gradient,
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: "1.3",
                letterSpacing: "-0.02em"
              }}
              animate={{
                backgroundPosition: ["0% 0%", "200% 0%", "0% 0%"],
                scale: [1, 1.005, 1],
              }}
              transition={{
                backgroundPosition: {
                  duration: isMobile ? 3 : 4,
                  repeat: Infinity,
                  ease: "linear"
                },
                scale: {
                  duration: isMobile ? 2 : 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
            >
              <span className="block mb-5 sm:mb-6 md:mb-8 lg:inline">Dream it. </span>
              <span className="block mb-5 sm:mb-6 md:mb-8 lg:inline">Strategize it. </span>
              <span className="block mb-5 sm:mb-0">Live it.</span>
            </motion.h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: animationDuration, delay: 0.3 }}
            className="mb-20 sm:mb-16 md:mb-20 lg:mb-24"
          >
            {/* Simplified button animation - removed continuous animations */}
            <motion.a 
              href="#contact" 
              className="inline-block text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-full text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold"
              style={{
                background: colors.gradient,
                boxShadow: `0 0 10px ${colors.primary}15`
              }}
              whileHover={{ 
                scale: 1.02,
                boxShadow: `0 0 20px ${colors.primary}40`,
              }}
              whileTap={{ scale: 0.98 }}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Get Started
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};